import { datadogRum } from "@datadog/browser-rum";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { User } from "api/api.typing";
import { fetchMe, getXSRFToken, requestLogout } from "api/user.api";
import { AxiosError } from "axios";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/routes.const";
import { useHubConnection } from "store/useHubConnection";
import { removeLocalStorageValue, setLocalStorageValue } from "utils/storage";

const LOGOUT_LS_EVENT = "PartnerPortalLogout";

export const useUser = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const startHub = useHubConnection((state) => state.start);

  const handleLogOutInOtherTab = async (ev: StorageEvent) => {
    if (ev.key !== LOGOUT_LS_EVENT) return;
    queryClient.removeQueries();
    navigate(ROUTES.LOGIN);
  };

  const listenToLogOutInLocalStorage = () => {
    window.addEventListener("storage", handleLogOutInOtherTab);
    return () => window.removeEventListener("storage", handleLogOutInOtherTab);
  };

  const { data, isLoading, error } = useQuery<User, AxiosError>({
    queryKey: ["user"],
    queryFn: async () => {
      const user = await fetchMe();
      if (user) {
        startHub();
        listenToLogOutInLocalStorage();
      }
      return user;
    },
    retry: 2,
  });

  return { data, isLoading, error };
};

export const useLogoutMutation = (redirectToLogin = true) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const stopHub = useHubConnection((state) => state.stopConnection);

  useEffect(() => {
    datadogRum.addAction("fetch-XSRF");
    getXSRFToken().catch(() => Promise.resolve());
  }, []);

  return useMutation<void, AxiosError>({
    mutationFn: requestLogout,
    onSuccess: async () => {
      datadogRum.addAction("logout-success", {
        email: queryClient.getQueryData<User>(["user"])?.email,
      });
    },
    onError: (error) => {
      datadogRum.addAction("logout-failed", {
        email: queryClient.getQueryData<User>(["user"])?.email,
        error: { status: error.response?.status, message: error.message, response: error.response?.data },
      });
    },
    onSettled: async () => {
      dispatchLogOutEvent();
      datadogRum.clearUser();

      queryClient.removeQueries(); // remove all queries
      if (redirectToLogin) {
        navigate(ROUTES.LOGIN);
      }

      stopHub();
    },
  });
};

const dispatchLogOutEvent = () => {
  setLocalStorageValue(LOGOUT_LS_EVENT, "true", "logout-event");
  removeLocalStorageValue(LOGOUT_LS_EVENT);
};
